// Conversation Memory System
// Stores and retrieves AI conversation history from Firestore

import { doc, setDoc, getDoc, updateDoc, arrayUnion } from 'firebase/firestore';
import { db } from './firebaseConfig';

/**
 * Ensure user document exists (required for subcollections)
 * @param {string} userId - User ID
 */
const ensureUserDocumentExists = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      // Create minimal user document if it doesn't exist
      await setDoc(userRef, {
        createdAt: new Date().toISOString(),
        lastActive: new Date().toISOString()
      }, { merge: true });
      console.log('✅ Created user document for subcollections');
    }
  } catch (error) {
    console.error('Error ensuring user document exists:', error);
    // Don't throw - this is a fallback
  }
};

/**
 * Save a conversation message to Firestore
 * @param {string} userId - User ID
 * @param {number} squareNumber - Square position
 * @param {Object} message - Message object { role, content, timestamp }
 */
export const saveConversationMessage = async (userId, squareNumber, message) => {
  try {
    // Ensure parent user document exists first
    await ensureUserDocumentExists(userId);
    
    const conversationRef = doc(db, 'users', userId, 'conversations', `square_${squareNumber}`);
    
    // Get existing conversation
    const conversationDoc = await getDoc(conversationRef);
    
    if (conversationDoc.exists()) {
      // Append to existing conversation
      await updateDoc(conversationRef, {
        messages: arrayUnion(message),
        lastMessageAt: new Date().toISOString(),
        messageCount: (conversationDoc.data().messageCount || 0) + 1
      });
    } else {
      // Create new conversation
      await setDoc(conversationRef, {
        squareNumber,
        messages: [message],
        createdAt: new Date().toISOString(),
        lastMessageAt: new Date().toISOString(),
        messageCount: 1
      });
    }
    
    console.log(`💾 Saved message for square ${squareNumber}`);
  } catch (error) {
    console.error('Error saving conversation message:', error);
    // Don't throw - let conversation continue even if save fails
  }
};

/**
 * Get conversation history for a specific square
 * @param {string} userId - User ID
 * @param {number} squareNumber - Square position
 * @returns {Array} - Array of message objects
 */
export const getConversationHistory = async (userId, squareNumber) => {
  try {
    const conversationRef = doc(db, 'users', userId, 'conversations', `square_${squareNumber}`);
    const conversationDoc = await getDoc(conversationRef);
    
    if (conversationDoc.exists()) {
      const data = conversationDoc.data();
      console.log(`📖 Loaded ${data.messages.length} messages for square ${squareNumber}`);
      return data.messages || [];
    }
    
    console.log(`📖 No previous conversation for square ${squareNumber}`);
    return [];
  } catch (error) {
    console.error('Error getting conversation history:', error);
    return []; // Return empty array on error
  }
};

/**
 * Get all conversations for a user (for context)
 * @param {string} userId - User ID
 * @returns {Array} - Array of conversation summaries
 */
export const getAllConversations = async (userId) => {
  try {
    const { collection, getDocs } = await import('firebase/firestore');
    const conversationsRef = collection(db, 'users', userId, 'conversations');
    const snapshot = await getDocs(conversationsRef);
    
    const conversations = [];
    snapshot.forEach((doc) => {
      const data = doc.data();
      conversations.push({
        squareNumber: data.squareNumber,
        messageCount: data.messageCount,
        lastMessageAt: data.lastMessageAt,
        // Don't include full messages - just metadata
      });
    });
    
    console.log(`📚 User has ${conversations.length} total conversations`);
    return conversations;
  } catch (error) {
    console.error('Error getting all conversations:', error);
    return [];
  }
};

/**
 * Format conversation history for Claude API
 * @param {Array} messages - Array of message objects
 * @returns {Array} - Formatted messages for Claude API
 */
export const formatMessagesForClaude = (messages) => {
  return messages.map(msg => ({
    role: msg.role, // 'user' or 'assistant'
    content: msg.content
  }));
};

/**
 * Get recent conversations for context (last 3)
 * Useful for providing continuity across different squares
 * @param {string} userId - User ID
 * @returns {Object} - Summary of recent conversations
 */
export const getRecentConversationSummary = async (userId) => {
  try {
    const { collection, getDocs, query, orderBy, limit } = await import('firebase/firestore');
    const conversationsRef = collection(db, 'users', userId, 'conversations');
    const q = query(conversationsRef, orderBy('lastMessageAt', 'desc'), limit(3));
    const snapshot = await getDocs(q);
    
    const summaries = [];
    snapshot.forEach((doc) => {
      const data = doc.data();
      // Get last user message and last AI response
      const messages = data.messages || [];
      const lastUserMsg = messages.filter(m => m.role === 'user').slice(-1)[0];
      const lastAiMsg = messages.filter(m => m.role === 'assistant').slice(-1)[0];
      
      if (lastUserMsg && lastAiMsg) {
        summaries.push({
          squareNumber: data.squareNumber,
          lastUserMessage: lastUserMsg.content.substring(0, 100) + '...', // Truncate
          lastAiMessage: lastAiMsg.content.substring(0, 100) + '...',
          when: data.lastMessageAt
        });
      }
    });
    
    return summaries;
  } catch (error) {
    console.error('Error getting recent conversation summary:', error);
    return [];
  }
};

/**
 * Check if this is a returning user for this square
 * @param {string} userId - User ID
 * @param {number} squareNumber - Square position
 * @returns {boolean} - True if user has previous conversation on this square
 */
export const isReturningToSquare = async (userId, squareNumber) => {
  try {
    const conversationRef = doc(db, 'users', userId, 'conversations', `square_${squareNumber}`);
    const conversationDoc = await getDoc(conversationRef);
    return conversationDoc.exists();
  } catch (error) {
    console.error('Error checking if returning to square:', error);
    return false;
  }
};
